import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { ErrorMessage, Field, FieldGroup, Fieldset, Label, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button } from "src/components/base/button";
import React from "react";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { ApiInternal } from "src/api/api_internal";
import { FullExtraDnsDomain } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link UpdateExtraDomainDialog}
 */
export type UpdateExtraDomainDialogProps = {
    /** The dns to update */
    dns: FullExtraDnsDomain;
    /** Callback for closing the dialog */
    onClose: () => void;
    /** Callback for adding the domain */
    onSubmit: () => void;
};

/**
 * A dialog for adding a new extra dns domain
 */
export default function UpdateExtraDomainDialog(props: UpdateExtraDomainDialogProps) {
    const [t] = useTranslation("dialog-update-extra-domain");

    const verifyForm = useForm({
        defaultValues: {
            domainName: props.dns.from_dns,
            description: props.dns.description,
        },
        validators: {
            onSubmitAsync: async (values) => {
                const res = await ApiInternal.dns.updateExtraDnsDomain(props.dns.uuid, {
                    from_dns: values.value.domainName,
                    description: values.value.description,
                });
                if (res.result === "Err") {
                    return {
                        fields: {
                            domainName: res.error.domain_invalid
                                ? t("error.invalid-domain")
                                : res.error.domain_already_exists
                                  ? t("error.domain-exists")
                                  : res.error.device_already_exists
                                    ? t("error.device-exists")
                                    : undefined,
                        },
                    };
                }
                props.onSubmit();
            },
        },
    });

    return (
        <Dialog onClose={props.onClose} open={true}>
            <Form onSubmit={verifyForm.handleSubmit}>
                <DialogTitle>{t("heading.update-extra-domain")}</DialogTitle>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <verifyForm.Field name={"domainName"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            max={255}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </verifyForm.Field>
                            <verifyForm.Field name={"description"}>
                                {(fieldApi) => (
                                    <Field>
                                        <Label>{t("label.description")}</Label>
                                        <Input
                                            max={255}
                                            value={fieldApi.state.value}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                    </Field>
                                )}
                            </verifyForm.Field>
                        </FieldGroup>
                    </Fieldset>
                </DialogBody>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("button.cancel")}</Button>
                    <Button type={"submit"} color={"blue"}>
                        {t("button.update")}
                    </Button>
                </DialogActions>
            </Form>
        </Dialog>
    );
}
